import { useEffect, useState } from 'react';
import { invitationsPubliquesApi } from '../../api/resources';
import {
  LONGUEUR_MOT_DE_PASSE,
  adresseApresAcceptation,
  estDuBureau,
  reprocheSaisie,
} from '../../lib/invitation';
import { C } from '../../theme/palette';
import { retiensOuvrier } from '../../lib/appareil-ouvrier';
import { GuideInstallation } from '../../pwa/GuideInstallation.jsx';
import { utiliseManifeste } from '../../pwa/manifeste';
import { EtapeCode } from '../auth/EtapeCode.jsx';
import { useModeConnexion } from '../../api/modeConnexion';
import { NOM_LOGICIEL } from '../../lib/marque';
import { trf } from '../../lib/i18n';

const ROLES = {
  OUVRIER: 'ouvrier',
  TECHNICIEN: 'chef d’équipe',
  SECRETAIRE: 'secrétariat',
  COMPTABLE: 'comptable',
  PATRON: 'direction',
};

const carte = {
  background: '#fff',
  border: '1px solid ' + C.border,
  borderRadius: 14,
  padding: 18,
  marginBottom: 14,
};

const etiquette = {
  display: 'block',
  fontSize: 11,
  fontWeight: 700,
  color: C.muted,
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  marginBottom: 6,
};

const champ = {
  width: '100%',
  boxSizing: 'border-box',
  border: '1px solid ' + C.border,
  borderRadius: 10,
  padding: '12px 12px',
  fontSize: 16,
  color: C.ink,
  background: '#fff',
  fontWeight: 600,
  outline: 'none',
};

const champCode = {
  ...champ,
  fontSize: 28,
  letterSpacing: 10,
  textAlign: 'center',
  fontFamily: 'monospace',
};

function Cadre({ children }) {
  return (
    <div style={{ minHeight: '100svh', background: C.bg, color: C.ink, padding: '22px 14px' }}>
      <div style={{ maxWidth: 520, margin: '0 auto' }}>{children}</div>
    </div>
  );
}

function Bouton({ children, onClick, disabled, type = 'button', secondaire }) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      style={{
        width: '100%',
        background: secondaire ? '#fff' : C.green,
        color: secondaire ? C.navy : '#fff',
        border: secondaire ? '1px solid ' + C.border : 'none',
        borderRadius: 12,
        padding: '14px 16px',
        fontSize: 15,
        fontWeight: 800,
        cursor: disabled ? 'wait' : 'pointer',
        opacity: disabled ? 0.65 : 1,
      }}
    >
      {children}
    </button>
  );
}

/**
 * Page que l'employé ouvre par le lien d'invitation reçu de son patron.
 *
 * <p>Il n'a pas encore d'accès : la page se suffit. Elle lui montre qui
 * l'invite et le laisse choisir son code à quatre chiffres — celui de la
 * connexion sur chantier — et, s'il le veut, une adresse et un mot de passe.
 * À la validation, son accès est ouvert et sa session aussi : il entre dans
 * son espace, où son contrat l'attend s'il reste à signer.
 *
 * <p>Quand le code à six chiffres vaut pour tous, l'ouvrier aussi choisit son
 * adresse et son mot de passe, et la session attend le code parti à cette
 * adresse : il la prouve.
 *
 * @param {object} props propriétés du composant
 * @param {string} props.jeton jeton porté par le lien
 * @returns {JSX.Element} la page
 */
export function PageInvitation({ jeton }) {
  const [page, setPage] = useState(null);
  const [refus, setRefus] = useState('');
  const [pin, setPin] = useState('');
  const [pin2, setPin2] = useState('');
  const [parEmail, setParEmail] = useState(false);
  const [email, setEmail] = useState('');
  const [motDePasse, setMotDePasse] = useState('');
  const [erreur, setErreur] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [acces, setAcces] = useState(null);
  // Acceptation faite, session pas encore ouverte : le code est attendu.
  const [enAttente, setEnAttente] = useState(null);
  const codeOuvrier = useModeConnexion()?.codeOuvrier === true;

  useEffect(() => {
    let annule = false;
    invitationsPubliquesApi
      .page(jeton)
      .then((p) => {
        if (annule) return;
        setPage(p);
        setEmail(p?.email || '');
      })
      .catch((e) => {
        if (!annule) {
          setRefus(
            e?.status === 404
              ? 'Ce lien d’invitation n’existe pas. Vérifie l’adresse, ou demande un nouveau lien à ton employeur.'
              : e?.message ||
                  'Cette invitation n’est plus valable. Demande un nouveau lien à ton employeur.',
          );
        }
      });
    return () => {
      annule = true;
    };
  }, [jeton]);

  useEffect(() => {
    document.title = page ? `Invitation — ${page.societe}` : 'Invitation';
  }, [page]);

  // Le bureau entre par son adresse et son mot de passe : pas de code à
  // quatre chiffres, qui lui ouvrait offres et factures avec le seul code de
  // la société, connu de tous les ouvriers.
  const bureau = estDuBureau(page?.role);
  // Quand le code à six chiffres vaut pour tous, l'ouvrier aussi entre par son
  // adresse et son mot de passe : le code à quatre chiffres n'ouvre plus rien.
  const adresseSeule = bureau || !codeOuvrier;

  const valide = async (e) => {
    e.preventDefault();
    const reproche = reprocheSaisie({
      pin,
      pin2,
      parEmail,
      email,
      motDePasse,
      bureau: adresseSeule,
    });
    if (reproche) {
      setErreur(reproche);
      return;
    }
    setErreur('');
    setEnvoi(true);
    try {
      const ouvert = await invitationsPubliquesApi.accepte(
        jeton,
        adresseSeule
          ? { email: email.trim(), motDePasse }
          : { pin, ...(parEmail ? { email: email.trim(), motDePasse } : {}) },
      );
      if (ouvert?.code?.codeRequis) {
        setEnAttente(ouvert);
        return;
      }
      setAcces(ouvert);
      if (adresseSeule) return;
      // Demain matin, ce téléphone le reconnaîtra : il ne tapera que ses
      // quatre chiffres.
      retiensOuvrier({
        codeEntreprise: ouvert?.codeSociete,
        employeId: ouvert?.compte?.employeeId,
        nom: ouvert?.compte?.nomComplet || [page?.prenom, page?.nom].filter(Boolean).join(' '),
        entreprise: page?.societe,
      });
      // L'icône qu'il va installer s'ouvrira à son nom : l'iPhone ne passe
      // rien de Safari à l'application installée, l'adresse de départ le dit.
      if (ouvert?.codeSociete && ouvert?.compte?.employeeId != null) {
        const code = encodeURIComponent(ouvert.codeSociete);
        const employe = ouvert.compte.employeeId;
        utiliseManifeste(`/api/public/manifeste/ouvrier?code=${code}&employe=${employe}`, {
          titre: NOM_LOGICIEL,
          depart: `/?ouvrier=${String(ouvert.codeSociete).toUpperCase()}.${employe}`,
        });
      }
    } catch (err) {
      setErreur(err?.message || 'L’accès n’a pas pu être ouvert. Réessaie dans un instant.');
    } finally {
      setEnvoi(false);
    }
  };

  if (refus) {
    return (
      <Cadre>
        <div style={{ ...carte, textAlign: 'center', padding: 32 }}>
          <div style={{ fontSize: 34, marginBottom: 10 }}>🔗</div>
          <h1 style={{ fontSize: 18, margin: '0 0 8px' }}>Invitation indisponible</h1>
          <p role="alert" style={{ fontSize: 14, color: C.grey, margin: 0, lineHeight: 1.5 }}>
            {refus}
          </p>
        </div>
      </Cadre>
    );
  }

  if (!page) {
    return (
      <Cadre>
        <div style={{ ...carte, textAlign: 'center', color: C.muted, fontSize: 14 }} role="status">
          Chargement…
        </div>
      </Cadre>
    );
  }

  const prenom = page.prenom || page.nom || '';

  if (enAttente && !acces) {
    return (
      <Cadre>
        <header style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 12, color: C.grey, fontWeight: 700 }}>Invitation</div>
          <div style={{ fontSize: 20, fontWeight: 900 }}>{page.societe}</div>
        </header>
        <section style={carte}>
          <EtapeCode etat={enAttente.code} onConnecte={() => setAcces(enAttente)} />
        </section>
      </Cadre>
    );
  }

  if (acces) {
    return (
      <Cadre>
        <div style={{ ...carte, textAlign: 'center' }}>
          <div style={{ fontSize: 34, marginBottom: 8 }}>✅</div>
          <h1 style={{ fontSize: 20, margin: '0 0 6px' }}>Bienvenue, {prenom} !</h1>
          <p style={{ fontSize: 14, color: C.grey, margin: 0, lineHeight: 1.5 }}>
            Ton accès à <b>{page.societe}</b> est ouvert. Tu es connecté.
          </p>
        </div>
        {bureau ? (
          <div style={carte}>
            <span style={etiquette}>Pour te reconnecter plus tard</span>
            <div style={{ fontSize: 14, lineHeight: 1.6 }}>
              Ouvre SwissTech et connecte-toi avec ton adresse e-mail et ton mot de passe, sur ton
              téléphone ou un ordinateur.
            </div>
          </div>
        ) : !acces.codeSociete ? (
          <>
            <GuideInstallation
              nom={NOM_LOGICIEL}
              apres="Elle s’ouvre comme une application : tu te connectes avec ton adresse e-mail et ton mot de passe."
            />
            <div style={carte}>
              <span style={etiquette}>Pour te reconnecter plus tard</span>
              <div style={{ fontSize: 14, lineHeight: 1.6 }}>
                Ouvre SwissTech et connecte-toi avec ton adresse e-mail et ton mot de passe, sur ton
                téléphone ou un ordinateur. Un code à 6 chiffres te sera ensuite envoyé par e-mail
                ou par SMS.
              </div>
            </div>
          </>
        ) : (
          <>
            <GuideInstallation
              nom={NOM_LOGICIEL}
              apres="Elle s’ouvre à ton nom : chaque jour, tu tapes juste tes 4 chiffres."
            />
            <div style={carte}>
              <span style={etiquette}>Pour te reconnecter plus tard</span>
              <div style={{ fontSize: 14, lineHeight: 1.6 }}>
                Ouvre l’icône <b>SwissTech</b> : ton nom est déjà là, tape tes quatre chiffres. Sur
                un autre téléphone : <b>Code ouvrier</b>, le code de l’entreprise ci-dessous, ton
                nom, puis tes quatre chiffres.
              </div>
              <div
                style={{
                  marginTop: 12,
                  background: C.navyXL,
                  border: '1px dashed ' + C.navy,
                  borderRadius: 12,
                  padding: '12px 14px',
                  textAlign: 'center',
                }}
              >
                <div style={{ fontSize: 11, color: C.grey, fontWeight: 700 }}>
                  Code de l’entreprise
                </div>
                <div
                  data-testid="code-societe"
                  style={{
                    fontSize: 26,
                    fontWeight: 900,
                    letterSpacing: 4,
                    fontFamily: 'monospace',
                    color: C.navy,
                  }}
                >
                  {acces.codeSociete || '—'}
                </div>
                <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>
                  Note-le : il te sera demandé avec ton code à quatre chiffres.
                </div>
              </div>
              {/* L'accès par e-mail venait d'être créé, mais l'écran ne parlait
                  que des quatre chiffres : l'ouvrier croyait que seul le code
                  marchait. */}
              {parEmail && email.trim() && (
                <div
                  data-testid="acces-email"
                  style={{ marginTop: 12, fontSize: 14, lineHeight: 1.6 }}
                >
                  {trf(
                    'Ou avec ton adresse e-mail {0} et ton mot de passe, depuis n’importe quel téléphone ou ordinateur : onglet « Connexion » de SwissTech.',
                    email.trim(),
                  )}
                </div>
              )}
            </div>
          </>
        )}
        {acces.contratASigner && (
          <div style={{ ...carte, background: C.amberL, border: '1px solid ' + C.amber }}>
            <div style={{ fontSize: 14, fontWeight: 800, color: C.amber }}>
              📄 Ton contrat de travail t’attend
            </div>
            <div style={{ fontSize: 13, color: C.grey, marginTop: 4, lineHeight: 1.5 }}>
              Relis-le, puis signe-le au doigt dans ton espace : c’est la dernière étape.
            </div>
          </div>
        )}
        <a
          href={adresseApresAcceptation(acces.contratASigner)}
          style={{
            display: 'block',
            textAlign: 'center',
            textDecoration: 'none',
            background: C.green,
            color: '#fff',
            borderRadius: 12,
            padding: '14px 16px',
            fontSize: 15,
            fontWeight: 800,
          }}
        >
          {acces.contratASigner ? 'Continuer → Signer mon contrat' : 'Entrer dans mon espace'}
        </a>
      </Cadre>
    );
  }

  return (
    <Cadre>
      <header style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 12, color: C.grey, fontWeight: 700 }}>Invitation</div>
        <div style={{ fontSize: 20, fontWeight: 900 }}>{page.societe}</div>
      </header>

      <section style={carte}>
        <h1 style={{ fontSize: 18, margin: '0 0 6px' }}>Bonjour {prenom},</h1>
        <p style={{ fontSize: 14, color: C.grey, margin: 0, lineHeight: 1.55 }}>
          {bureau ? (
            <>
              <b style={{ color: C.ink }}>{page.societe}</b> t’invite à rejoindre SwissTech,
              l’application de l’entreprise, comme{' '}
              <b style={{ color: C.ink }}>{ROLES[page.role]}</b>. Choisis ton adresse e-mail et un
              mot de passe : c’est avec eux que tu te connecteras, sur ton téléphone ou un
              ordinateur.
            </>
          ) : adresseSeule ? (
            <>
              <b style={{ color: C.ink }}>{page.societe}</b> t’invite à rejoindre SwissTech,
              l’application de l’entreprise, comme{' '}
              <b style={{ color: C.ink }}>{ROLES[page.role] || 'ouvrier'}</b>. Choisis ton adresse
              e-mail et un mot de passe : c’est avec eux que tu te connecteras, sur ton téléphone ou
              un ordinateur.
            </>
          ) : (
            <>
              <b style={{ color: C.ink }}>{page.societe}</b> t’invite à rejoindre SwissTech, son
              application de chantier, comme{' '}
              <b style={{ color: C.ink }}>{ROLES[page.role] || 'ouvrier'}</b>. Choisis ton code :
              c’est avec lui que tu te connecteras sur le chantier.
            </>
          )}
        </p>
      </section>

      <form onSubmit={valide} noValidate>
        {!adresseSeule && (
          <section style={carte}>
            <label style={etiquette} htmlFor="pin">
              Mon code à 4 chiffres
            </label>
            <input
              id="pin"
              type="password"
              inputMode="numeric"
              autoComplete="off"
              maxLength={4}
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
              placeholder="••••"
              style={champCode}
            />
            <label style={{ ...etiquette, marginTop: 12 }} htmlFor="pin2">
              Encore une fois, pour vérifier
            </label>
            <input
              id="pin2"
              type="password"
              inputMode="numeric"
              autoComplete="off"
              maxLength={4}
              value={pin2}
              onChange={(e) => setPin2(e.target.value.replace(/\D/g, '').slice(0, 4))}
              placeholder="••••"
              style={champCode}
            />
            <div style={{ fontSize: 12, color: C.muted, marginTop: 8, lineHeight: 1.5 }}>
              Évite 0000 ou 1234. Ton code reste secret : personne ne te le demandera.
            </div>
          </section>
        )}

        <section style={carte}>
          {!adresseSeule && (
            <label
              style={{ display: 'flex', gap: 10, alignItems: 'flex-start', cursor: 'pointer' }}
            >
              <input
                type="checkbox"
                checked={parEmail}
                onChange={(e) => setParEmail(e.target.checked)}
                style={{ width: 20, height: 20, marginTop: 1 }}
              />
              <span style={{ fontSize: 14, lineHeight: 1.5 }}>
                <b>Ajouter un accès par e-mail et mot de passe</b>
                <br />
                <span style={{ color: C.grey, fontSize: 13 }}>
                  Facultatif. Pratique pour te connecter depuis un ordinateur.
                </span>
              </span>
            </label>
          )}
          {(parEmail || adresseSeule) && (
            <div style={{ marginTop: adresseSeule ? 0 : 12 }}>
              <label style={etiquette} htmlFor="email">
                Adresse e-mail
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                style={champ}
              />
              <label style={{ ...etiquette, marginTop: 12 }} htmlFor="motDePasse">
                Mot de passe ({LONGUEUR_MOT_DE_PASSE} caractères au moins)
              </label>
              <input
                id="motDePasse"
                type="password"
                autoComplete="new-password"
                value={motDePasse}
                onChange={(e) => setMotDePasse(e.target.value)}
                style={champ}
              />
            </div>
          )}
        </section>

        {erreur && (
          <div
            role="alert"
            style={{
              background: C.redL,
              color: C.red,
              border: '1px solid ' + C.red,
              borderRadius: 10,
              padding: '10px 12px',
              fontSize: 13,
              fontWeight: 700,
              marginBottom: 14,
              lineHeight: 1.5,
            }}
          >
            {erreur}
          </div>
        )}

        <Bouton type="submit" disabled={envoi}>
          {envoi ? 'Ouverture de ton accès…' : '✓ Valider et entrer'}
        </Bouton>
        <p style={{ fontSize: 11, color: C.muted, textAlign: 'center', marginTop: 12 }}>
          {adresseSeule
            ? 'Ce lien ne sert qu’une fois. Ton mot de passe est enregistré chiffré.'
            : 'Ce lien ne sert qu’une fois. Ton code est enregistré chiffré.'}
        </p>
      </form>
    </Cadre>
  );
}
